import { useEffect, useMemo, useState } from 'react';

import { ButtonOutlinedErrorSmall, ButtonOutlinedSmall } from '@/components/Button';
import { Select } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import Modal from '@/components/Modal/Modal';
import { countTransactionsByCategory } from '@/store/expenses-store/transactions/transactions.api';
import type { TransactionCategory } from '@/store/expenses-store/transactions/transactions.dto';

import s from './DeleteCategoryModal.module.scss';

interface DeleteCategoryModalProps {
  open: boolean;
  onClose: () => void;
  /** The category to delete. */
  category: TransactionCategory | null;
  /** Every category of the vessel, the one being deleted included — it is filtered out here. */
  categories: TransactionCategory[];
  /** Receives the category that takes the entries over; undefined leaves the choice to wallet. */
  onConfirm: (targetCategoryId: string | undefined) => void;
  deleting?: boolean;
}

/**
 * Deleting a category never drops its history: the transactions and planned budgets filed
 * under it move to a category picked here, defaulting to the vessel's protected "Other".
 * Where a year is planned on both sides wallet sums the two plans, so the year's total holds.
 * A category with no transactions has nothing to hand over, so it is a plain confirmation.
 */
export function DeleteCategoryModal({
  open,
  onClose,
  category,
  categories,
  onConfirm,
  deleting = false,
}: DeleteCategoryModalProps) {
  if (!category) return null;

  return (
    <Modal open={open} onClose={onClose} title="Delete Category" size="full">
      <DeleteCategoryForm
        category={category}
        categories={categories}
        onCancel={onClose}
        onConfirm={onConfirm}
        deleting={deleting}
      />
    </Modal>
  );
}

interface DeleteCategoryFormProps {
  category: TransactionCategory;
  categories: TransactionCategory[];
  onCancel: () => void;
  onConfirm: (targetCategoryId: string | undefined) => void;
  deleting: boolean;
}

/** What is filed under the category: until the count lands neither wording is on screen. */
type Entries = 'counting' | 'empty' | 'present';

function DeleteCategoryForm({
  category,
  categories,
  onCancel,
  onConfirm,
  deleting,
}: DeleteCategoryFormProps) {
  const options = useMemo(
    () =>
      categories.filter((c) => c.id !== category.id).map((c) => ({ label: c.title, value: c.id })),
    [categories, category.id],
  );

  // Mounted fresh on every open (the modal unmounts its children when closed), so the
  // fallback is the starting point each time rather than the previous pick.
  const [targetId, setTargetId] = useState(
    () => categories.find((c) => c.is_default)?.id ?? options[0]?.value ?? '',
  );
  const [entries, setEntries] = useState<Entries>('counting');

  useEffect(() => {
    let live = true;
    countTransactionsByCategory(category.id)
      .then((count) => {
        if (live) setEntries(count > 0 ? 'present' : 'empty');
      })
      // The count may only spare the question, never hide it: unanswered, the picker stays
      // and wallet moves whatever it finds.
      .catch(() => {
        if (live) setEntries('present');
      });

    return () => {
      live = false;
    };
  }, [category.id]);

  const counting = entries === 'counting';
  const needsTarget = entries === 'present';
  const target = categories.find((c) => c.id === targetId) ?? null;

  return (
    <div className={s.content}>
      <p className={s.text}>
        {needsTarget ? (
          <>
            Delete <strong>{category.title}</strong>? Its transactions and planned budgets move to
            the category you pick below. Nothing is lost, but the category itself can’t be brought
            back.
          </>
        ) : (
          <>
            Delete <strong>{category.title}</strong>?
            {!counting &&
              ' No transactions are filed under it. The category can’t be brought back.'}
          </>
        )}
      </p>

      {counting && (
        <LoaderSpinner size={18} fullHeight={false} label="Checking what is filed under it…" />
      )}

      {needsTarget && (
        <Select<string>
          label="Move To"
          options={options}
          value={targetId}
          onChange={setTargetId}
          placeholder="Select a category"
        />
      )}

      <div className={s.actions}>
        <ButtonOutlinedErrorSmall
          type="button"
          disabled={deleting || counting || (needsTarget && !target)}
          onClick={() => onConfirm(needsTarget ? target?.id : undefined)}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </ButtonOutlinedErrorSmall>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
      </div>
    </div>
  );
}
